import { Badge, EmptyState, SkeletonRows } from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEventQuery } from '../../../api/queries.ts';
import { NotWired } from '../../../components/not-wired.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { formatDateTime } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/events/$eventId')({
  component: EventDetailPage,
  errorComponent: RouteErrorPanel,
});

function EventDetailPage() {
  const { eventId } = Route.useParams();
  const event = useEventQuery(eventId);

  if (event.isLoading) {
    return <SkeletonRows rows={5} className="p-4" />;
  }
  if (event.isError || event.data === undefined) {
    return <EmptyState kind="error" className="m-4" title="Could not load this event" />;
  }

  const detail = event.data;

  return (
    <>
      <PageHeader
        title={detail.name}
        description={
          [
            formatDateTime(detail.startsAt),
            detail.locationText,
            [detail.city, detail.country].filter(Boolean).join(', ') || null,
          ]
            .filter((part): part is string => part !== null && part !== '')
            .join(' · ') || 'No date or location recorded'
        }
        actions={<Badge variant="muted">{detail.kind}</Badge>}
      />
      <PageBody>
        {detail.notes === null ? null : (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{detail.notes}</p>
        )}

        <NotWired
          title="People I met here"
          blockedOn="contacts.list.metAtEventId returns 422 — the repository has no met-at filter yet"
        >
          The event says {detail.contactCount} contact(s) are linked to it, but there is no endpoint
          that will list them. Showing an arbitrary page of contacts here would be worse than
          showing none.
        </NotWired>
      </PageBody>
    </>
  );
}
